/*
 * The pay block on the bag: the QR panel, and the `upi://` intent beside it.
 *
 * WHAT THIS MODULE MAY NEVER DO
 * -----------------------------
 * Decide an amount. `utils/qr.py:upi_uri` is explicit that the number in a payment
 * request is `pricing.quote()["total_payable"]` and nothing else
 * (docs/BUILD_DECISIONS.md section 4), and that it must reach the intent from the
 * same server-side quote the order will be booked from. So the link this module
 * touches was BUILT by the server, and the only thing ever written into it is a
 * total that came back from `pricing.quote` in the same breath. No arithmetic
 * happens in this file.
 *
 * Nor may anything here say that money arrived. There is no gateway; a pasted UTR is
 * a string the customer typed (section 7). The panel offers a way to pay and the
 * page records a reference — the counter confirms it.
 *
 * WHY A STALE TOTAL HIDES THE SQUARE INSTEAD OF UPDATING IT
 * ---------------------------------------------------------
 * The QR is a `<path>` the server drew with `pyqrcode` around one exact amount, and
 * a QR cannot be edited: change the total and the square still encodes the old one.
 * A customer who scans it pays the wrong figure and then argues at the counter with
 * a bank reference that proves they paid what our own page showed them. So when a
 * re-quote moves the total, the square is withdrawn and the panel says why. The deep
 * link IS rebuilt, because its amount is a query parameter and can be made true.
 *
 * In the build as shipped this is a safety net rather than a daily path: applying a
 * privilege card on the bag is a GET form that reloads the page, so the server draws
 * a fresh square with a fresh total. The net is here because the day someone wires
 * an in-place re-quote into this page, the failure it prevents is a payment.
 */

import { all, empty, one } from "./nav.js";

/** The two labels on the disclosure, from the approved mockup. */
const SHOW = "Paying from another phone? Show QR";
const HIDE = "Hide QR";

/* ── the intent link ───────────────────────────────────────────────────────── */

/**
 * Rewrite `am=` on a `upi://pay?…` link.
 *
 * Split on the first "?" rather than handing the string to `URL`: `upi:` is not a
 * special scheme, and browsers disagree about what `new URL("upi://pay?pa=x")`
 * parses `pathname` and `host` into. The query is all we need and
 * `URLSearchParams` is exact about it.
 *
 * `am` is written to two decimals for the reason `qr.py` writes it that way: some
 * UPI apps read "2294.0" as one paisa short of the rupee amount.
 */
function reprice(link, total) {
	const href = link.getAttribute("href") || "";
	const cut = href.indexOf("?");
	if (href.slice(0, 4) !== "upi:" || cut === -1) return;

	const amount = Number(total);
	if (!isFinite(amount) || amount <= 0) return;

	const params = new URLSearchParams(href.slice(cut + 1));
	params.set("am", amount.toFixed(2));

	// `URLSearchParams.toString()` is form-encoding, and a UPI intent is not a form:
	// it turns the VPA's "@" into %40 and every space in the payee name into "+".
	// Both are legal to a strict parser and both are read wrong by at least one real
	// UPI app, which is exactly why `qr.py` writes "@" raw and "%20" for a space. The
	// two are put back. A literal "+" inside a value is unaffected — form-encoding
	// has already written that one as %2B.
	const query = params.toString().replace(/%40/g, "@").replace(/\+/g, "%20");
	link.setAttribute("href", href.slice(0, cut + 1) + query);
}

/* ── the disclosure ────────────────────────────────────────────────────────── */

/**
 * The QR panel is a `<details>`, so it opens and closes with no script at all.
 * All this adds is the mockup's label swap, which is the only part of it that
 * needs to know whether the panel is open.
 */
function bindDisclosure(panel) {
	const summary = one("summary", panel);
	if (!summary) return;

	function label() {
		summary.textContent = panel.open ? HIDE : SHOW;
	}

	panel.addEventListener("toggle", label);
	label();
}

/**
 * Open the QR when the customer chooses to pay by UPI, close it when they choose the
 * counter.
 *
 * Only on a real change, never on load: "Pay now by UPI" is the checked default when
 * UPI is on, and a panel that unfolded itself on every page load would push the
 * Place-the-order button below the fold for everybody.
 */
function bindPaymentChoice(panel) {
	all('input[name="payment_method"]').forEach(function (radio) {
		radio.addEventListener("change", function () {
			if (!radio.checked) return;
			panel.open = radio.value === "UPI";
		});
	});
}

/* ── a total that moved ────────────────────────────────────────────────────── */

/**
 * Withdraw the square and say why. See the module docstring — a QR is drawn around
 * one amount and cannot be corrected in place.
 */
function markStale(panel) {
	const code = one(".sb-qr-code", panel);
	if (!code || code.hasAttribute("data-sb-stale")) return;

	code.setAttribute("data-sb-stale", "1");
	empty(code);

	const note = document.createElement("p");
	note.className = "sb-cap";
	note.textContent =
		"The total changed, so this QR is out of date. Use the blue button to pay, or reload the page for a fresh square.";
	code.appendChild(note);
}

/**
 * The amount printed beside the square ("Pay ₹2,294 to shop@bank").
 *
 * It is removed rather than recalculated: the figure is the server's, formatted the
 * server's way, and the honest thing to show next to a withdrawn QR is the button —
 * not a number this file re-typeset.
 */
function dropStaleAmount(panel) {
	const vpa = one(".sb-qr-vpa", panel);
	if (vpa && vpa.parentNode) vpa.parentNode.removeChild(vpa);
}

/* ── boot ──────────────────────────────────────────────────────────────────── */

export function init() {
	const panel = one("[data-sb-qr]");
	if (!panel) return;

	bindDisclosure(panel);
	bindPaymentChoice(panel);

	const link = one('a[href^="upi:"]', panel);

	// `set.js` fires this after every re-quote, and it carries the ledger the server
	// just returned. `total_payable` is the only key read from it, and it is read
	// rather than derived (section 4).
	document.addEventListener("sb:quote", function (event) {
		const quote = event.detail || {};
		if (quote.total_payable === undefined) return;
		if (link) reprice(link, quote.total_payable);
		markStale(panel);
		dropStaleAmount(panel);
	});
}

/** Exported so the payment block's amount can be asserted in a browser test. */
export { reprice };
